/** Per-permission session-storage tracking, shared by every PermissionFlow. */

/** Blocked card display cap per tab session. */
const MAX_BLOCKED_PROMPTS_PER_SESSION = 2;

function promptShownKey(permissionId: string): string {
  return `jiopc-${permissionId}-prompt-shown`;
}

function blockedCountKey(permissionId: string): string {
  return `jiopc-${permissionId}-blocked-shown-count`;
}

export function hasShownPrompt(permissionId: string): boolean {
  try {
    return sessionStorage.getItem(promptShownKey(permissionId)) === '1';
  } catch {
    return false;
  }
}

export function markPromptShown(permissionId: string): void {
  try {
    sessionStorage.setItem(promptShownKey(permissionId), '1');
  } catch {
    /* empty */
  }
}

/** How many times the blocked card has already been displayed this session. A corrupt stored
 * value reads as 0 rather than NaN, so it can't silently defeat the display cap. */
export function blockedPromptCount(permissionId: string): number {
  try {
    const count = Number(sessionStorage.getItem(blockedCountKey(permissionId)) ?? '0');
    return Number.isFinite(count) ? count : 0;
  } catch {
    return 0;
  }
}

/** True once the blocked card has hit its display cap. */
export function hasExhaustedBlockedPrompts(permissionId: string): boolean {
  return blockedPromptCount(permissionId) >= MAX_BLOCKED_PROMPTS_PER_SESSION;
}

export function recordBlockedPromptShown(permissionId: string): void {
  try {
    sessionStorage.setItem(
      blockedCountKey(permissionId),
      String(blockedPromptCount(permissionId) + 1),
    );
  } catch {
    /* empty */
  }
}

/** Closing the blocked card exhausts it for this tab session. */
export function exhaustBlockedPrompts(permissionId: string): void {
  try {
    sessionStorage.setItem(blockedCountKey(permissionId), String(MAX_BLOCKED_PROMPTS_PER_SESSION));
  } catch {
    /* empty */
  }
}

/** Called when the permission is no longer denied; restarts the count. */
export function resetBlockedPromptCount(permissionId: string): void {
  try {
    sessionStorage.removeItem(blockedCountKey(permissionId));
  } catch {
    /* empty */
  }
}

function restorePendingKey(permissionId: string): string {
  return `jiopc-${permissionId}-restore-pending`;
}

/** Grant keys that were seen blocked this tab session and still owe a "connected" toast once restored. */
export function restorePendingKeys(permissionId: string): string[] {
  try {
    const parsed: unknown = JSON.parse(sessionStorage.getItem(restorePendingKey(permissionId)) ?? '[]');
    return Array.isArray(parsed) ? parsed.filter((key): key is string => typeof key === 'string') : [];
  } catch {
    return [];
  }
}

export function setRestorePendingKeys(permissionId: string, keys: string[]): void {
  try {
    if (keys.length) sessionStorage.setItem(restorePendingKey(permissionId), JSON.stringify(keys));
    else sessionStorage.removeItem(restorePendingKey(permissionId));
  } catch {
    /* empty */
  }
}

export function clearPromptShown(permissionId: string): void {
  try {
    sessionStorage.removeItem(promptShownKey(permissionId));
    sessionStorage.removeItem(blockedCountKey(permissionId));
    // Logout must not hand a pending "connected" toast to whoever signs in next on this tab.
    sessionStorage.removeItem(restorePendingKey(permissionId));
  } catch {
    /* empty */
  }
}
